import { fetchQueryEstimates } from '../main/dollars-to-queries';
import { debounce } from '../main/utils';
import gtmSelectItem from './google-tag-manager/select-item';

const serviceSelect = document.querySelector(
  '.js-credit-widget select[name="service_credit_widget_display"]',
) as HTMLSelectElement;
const amountSelect = document.querySelector(
  '.js-credit-widget select[name="amount_display"]',
) as HTMLSelectElement;
const otherAmountInput = document.querySelector(
  '.js-credit-widget__input',
) as HTMLInputElement;
const creditWidgetErrorMsg = document.querySelector(
  '.js-credit-widget .form-error',
);
const purchaseButton = document.querySelector(
  '.js-credit-widget__purchase-btn',
) as HTMLButtonElement;
const stripePriceIdInput = document.querySelector(
  '.js-credit-widget input[name="stripe_price_id"]',
) as HTMLInputElement;
const stripeQuantityInput = document.querySelector(
  '.js-credit-widget input[name="stripe_quantity"]',
) as HTMLInputElement;
const serviceCreditWidgetInput = document.querySelector(
  '.js-credit-widget input[name="service_credit_widget"]',
) as HTMLInputElement;

// Display or hide the other amount input
const showOtherAmountInput = () => {
  const otherAmountDiv = document.querySelector(
    '.js-credit-widget__other-amount',
  ) as HTMLDivElement;

  if (amountSelect.value === 'other') {
    otherAmountDiv.classList.remove('d-none');
    validateOtherAmount(false);
  } else {
    otherAmountDiv.classList.add('d-none');
    creditWidgetErrorMsg?.classList.add('d-none');
    purchaseButton.disabled = false;
    updateStripeFields(); // Update Stripe fields when selecting pre-defined amount
  }
};
amountSelect.addEventListener('input', showOtherAmountInput);

// The amount input is not required, so an empty box passes checkValidity().
// An empty box therefore counts as invalid only when flagEmpty is set, which
// keeps the message off the page until the customer tries to purchase.
const validateOtherAmount = (flagEmpty: boolean): boolean => {
  // A number input holding unparseable text reports an empty value, so ask
  // badInput to tell "nothing typed" apart from "typed something invalid".
  const isEmpty =
    otherAmountInput.value === '' && !otherAmountInput.validity.badInput;
  const isInvalid =
    amountSelect.value === 'other' &&
    (isEmpty ? flagEmpty : !otherAmountInput.checkValidity());

  purchaseButton.disabled = isInvalid;
  creditWidgetErrorMsg?.classList.toggle('d-none', !isInvalid);
  return !isInvalid;
};

otherAmountInput.addEventListener('input', () => validateOtherAmount(false));

// Validate on click, not submit: the add-to-cart tracking listener posts the
// form itself with form.submit(), which a submit listener cannot cancel.
purchaseButton.addEventListener('click', (e) => {
  if (!validateOtherAmount(true)) {
    e.preventDefault();
    otherAmountInput.focus();
  }
});

// Get the Service-Credit Stripe price ID from the form data attribute
const getServiceCreditPriceId = (): string => {
  const form = document.querySelector('.js-credit-widget') as HTMLFormElement;
  return form?.dataset.serviceCreditPriceId || '';
};

// Update service credit widget field when service selection changes
const updateServiceCreditWidget = () => {
  if (serviceSelect && serviceCreditWidgetInput) {
    serviceCreditWidgetInput.value = serviceSelect.value;
  }
};

// Update Stripe form fields when amount changes
const updateStripeFields = () => {
  const rawAmount =
    amountSelect.value === 'other'
      ? otherAmountInput.value
      : amountSelect.value;

  const amount = parseFloat(rawAmount);

  if (!isNaN(amount) && amount > 0) {
    const stripePriceId = getServiceCreditPriceId();
    if (stripePriceIdInput) {
      stripePriceIdInput.value = stripePriceId;
    }
    if (stripeQuantityInput) {
      // For Service-Credit, use the raw amount preserving fractional parts
      // The backend will validate and reject fractional amounts appropriately
      stripeQuantityInput.value = amount.toString();
    }
  } else {
    // Clear fields if no valid amount
    if (stripePriceIdInput) {
      stripePriceIdInput.value = '';
    }
    if (stripeQuantityInput) {
      stripeQuantityInput.value = '';
    }
  }
};

// The estimate endpoint refuses an amount outside the purchase range, so an
// out-of-range entry is the input's own error to report, not a failed lookup.
const hasPurchasableAmount = (): boolean =>
  amountSelect.value !== 'other' ||
  (otherAmountInput.value !== '' && otherAmountInput.checkValidity());

// Calculate the number of queries per price
const showQueries = async (sendGtmEvent = false) => {
  const creditWidgetQueries = document.querySelector(
    '.js-credit-widget__queries',
  );
  const creditWidgetQueriesMinfraud = document.querySelector(
    '.js-credit-widget__queries--minfraud',
  );
  const scoreQueries = document.querySelector(
    '.js-credit-widget-minfraud-score',
  );
  const insightsQueries = document.querySelector(
    '.js-credit-widget-minfraud-insights',
  );
  const factorsQueries = document.querySelector(
    '.js-credit-widget-minfraud-factors',
  );

  if (creditWidgetQueriesMinfraud) {
    const amount =
      parseInt(amountSelect.value) || otherAmountInput.valueAsNumber;

    const selectedService = {
      displayName: 'minFraud',
      name: 'minFraud',
    };

    if (hasPurchasableAmount()) {
      const json = await fetchQueryEstimates(amount);
      // The lookup logs its own failures. Hiding the block beats leaving the
      // previous amount's counts up as if they were current.
      creditWidgetQueriesMinfraud.classList.toggle('d-none', !json);

      interface QueriesProps {
        element: Element;
        serviceName: string;
        label: string;
      }

      const queriesAmount = ({ element, label, serviceName }: QueriesProps) => {
        const service = json?.find((item) => item.name === serviceName);
        if (!service) {
          // A response that omits the service is a name mismatch worth
          // reporting.
          if (json) console.error(`No query estimate for ${serviceName}`);
          element.textContent = `${label}: estimate unavailable`;
          return;
        }
        // The server commifies the estimate already.
        element.textContent = `${service.queries_remaining} ${label}`;
      };

      if (scoreQueries) {
        queriesAmount({
          element: scoreQueries,
          label: 'minFraud Score',
          serviceName: 'minfraud-score',
        });
      }

      if (insightsQueries) {
        queriesAmount({
          element: insightsQueries,
          label: 'minFraud Insights',
          serviceName: 'minfraud-insights',
        });
      }

      if (factorsQueries) {
        queriesAmount({
          element: factorsQueries,
          label: 'minFraud Factors',
          serviceName: 'minfraud-factors',
        });
      }

      const service = {
        display_name: 'minFraud',
        enabled: 1,
        name: 'minFraud',
        price: amount.toString(),
        queries_remaining: '1', // Hardcoding since we don't know the exact minFraud service
        service_id: 0,
      };

      //Add unit price to form element for add_to_cart gtm event
      (
        document.querySelector('.js-credit-widget') as HTMLFormElement
      ).dataset.unitPrice = amount.toString();

      if (sendGtmEvent) {
        gtmSelectItem({
          selectedService,
          service,
        });
      }
    } else {
      creditWidgetQueriesMinfraud.classList.add('d-none');
    }
  }

  if (creditWidgetQueries) {
    const amount =
      parseInt(amountSelect.value) || otherAmountInput.valueAsNumber;

    const selectedService = {
      displayName: serviceSelect.options[serviceSelect.selectedIndex].text,
      name: serviceSelect.options[serviceSelect.selectedIndex].dataset.name,
    };

    if (hasPurchasableAmount()) {
      const json = await fetchQueryEstimates(amount);
      const service = json?.find((item) => item.name === selectedService.name);
      if (!service) {
        // A response that omits the service is a name mismatch worth
        // reporting.
        if (json)
          console.error(`No query estimate for ${selectedService.name}`);
        creditWidgetQueries.textContent = 'Estimate unavailable';
        return;
      }

      //Add unit price to form element for add_to_cart gtm event
      (
        document.querySelector('.js-credit-widget') as HTMLFormElement
      ).dataset.unitPrice = service.price.replace('$', '');

      creditWidgetQueries.textContent = `${service.queries_remaining} ${selectedService.displayName}`;

      // If this is the initial load, don't send a select item GTM event
      if (sendGtmEvent) {
        gtmSelectItem({
          selectedService,
          service,
        });
      }
    } else {
      creditWidgetQueries.textContent = '';
    }
  }
};

const debouncedShowQueries = debounce(() => showQueries(true), 300);

serviceSelect?.addEventListener('input', () => {
  showQueries(true);
  updateServiceCreditWidget();
});
serviceSelect?.removeAttribute('disabled');
amountSelect.addEventListener('input', () => {
  showQueries(true);
  updateStripeFields();
});
amountSelect.removeAttribute('disabled');
otherAmountInput.addEventListener('input', () => {
  debouncedShowQueries();
  updateStripeFields();
});
otherAmountInput.removeAttribute('disabled');
showQueries();
updateStripeFields();
updateServiceCreditWidget();
// Enabled last: the templates ship the button disabled so that a page whose
// script never ran cannot post an empty stripe_price_id.
purchaseButton.removeAttribute('disabled');
